import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import {
  QrCodeScanner as QrCodeScannerIcon,
  LockOutlined as LockOutlinedIcon,
  PhoneIphone as PhoneIphoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const highlights = [
    {
      icon: <QrCodeScannerIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'It tells you what a code holds',
      description:
        'CodePeek reads a QR code or barcode and shows you the link, Wi-Fi login, contact or product number inside it — before you tap anything.',
    },
    {
      icon: <LockOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Nothing leaves your phone',
      description:
        'Every scan is decoded on the device. No image and no scanned text is sent to a server, so there is nothing for us or anyone else to see.',
    },
    {
      icon: <PhoneIphoneIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Free, and works offline',
      description:
        'Use it straight in the browser or install it as an app on Android and iPhone. Once installed it keeps working with no connection.',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Teniqua Systems
          </Typography>
          <Typography variant="h4" sx={{ mb: 4, opacity: 0.9 }}>
            A small studio building simple, private apps
          </Typography>
          <Typography variant="h6" sx={{ mb: 6, opacity: 0.85, maxWidth: 760, mx: 'auto' }}>
            We make everyday tools that do one thing well and respect the person
            using them. No accounts you do not need, no data grabs, no clutter.
            Our first app, CodePeek, is live now — and there is more on the way.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open('https://codepeek.teniquasystems.com/', '_blank', 'noopener')}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'grey.100' },
              }}
            >
              Try CodePeek
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/projects')}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              See what we build
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CodePeek highlight */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h2" textAlign="center" gutterBottom>
          Meet CodePeek
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: 720, mx: 'auto' }}>
          A QR and barcode reader that shows you what is really inside a code —
          the real domain behind a link included — so you can spot a dodgy one
          before you open it.
        </Typography>

        <Grid container spacing={4}>
          {highlights.map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" gutterBottom>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box textAlign="center" sx={{ mt: 5 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => window.open('https://codepeek.teniquasystems.com/', '_blank', 'noopener')}
            sx={{ px: 4, py: 1.5 }}
          >
            Open CodePeek
          </Button>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: theme.palette.grey[100], py: 8 }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" gutterBottom>
              Got an idea, or a bug to report?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              We read every message. Tell us what you would like us to build next,
              or let us know if something is not working.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{ px: 4, py: 1.5 }}
            >
              Get in touch
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
